import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import * as api from '../../services/api';
import { Users, FileText, LogOut, Plus, Search, Eye, Edit, Trash2, Mail, Settings, Save, X, Eye as EyeIcon, EyeOff, Download, ChevronDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../components/ui/sonner';
import ClientFormModal from '../../components/admin/ClientFormModal';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Fetch clients and unread messages on mount
  useEffect(() => {
    fetchClients();
    fetchUnreadMessages();
  }, []);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('[data-testid="export-btn"]') && !event.target.closest('[data-testid="export-pdf-btn"]') && !event.target.closest('[data-testid="export-excel-btn"]')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showExportMenu]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const data = await api.getAllClients();
      setClients(data);
    } catch (error) {
      toast.error('Erreur de chargement des clients');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadMessages = async () => {
    try {
      const data = await api.getUnreadMessagesCount();
      setUnreadMessages(data.count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSaveAdminPassword = async () => {
    if (!adminPassword.trim()) {
      toast.error('Le mot de passe ne peut pas être vide');
      return;
    }
    
    setSavingPassword(true);
    try {
      await api.updateAdminPassword(adminPassword);
      toast.success('Mot de passe admin mis à jour avec succès');
      setShowPasswordModal(false);
      setAdminPassword('');
    } catch (error) {
      console.error('Error updating admin password:', error);
      toast.error('Erreur lors de la mise à jour du mot de passe');
    } finally {
      setSavingPassword(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approuvé':
        return 'bg-green-100 text-green-800';
      case 'En cours':
        return 'bg-green-100 text-green-800';
      case 'En attente':
        return 'bg-yellow-100 text-yellow-800';
      case 'Refusé':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredClients = clients.filter(client => {
    const searchLower = searchTerm.toLowerCase();
    return (
      client.firstName.toLowerCase().includes(searchLower) ||
      client.lastName.toLowerCase().includes(searchLower) ||
      client.email.toLowerCase().includes(searchLower) ||
      client.id.toLowerCase().includes(searchLower)
    );
  });

  const handleViewClient = (client) => {
    navigate(`/admin/client/${client.id}`);
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setShowFormModal(true);
  };

  const handleDeleteClient = async (clientId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) {
      try {
        await api.deleteClient(clientId);
        toast.success('Client supprimé avec succès');
        fetchClients();
      } catch (error) {
        toast.error('Erreur lors de la suppression du client');
        console.error(error);
      }
    }
  };

  const handleAddClient = () => {
    setEditingClient(null);
    setShowFormModal(true);
  };

  const handleSaveClient = async (clientData) => {
    try {
      if (editingClient) {
        await api.updateClient(editingClient.id, clientData);
        toast.success('Client mis à jour avec succès');
      } else {
        await api.createClient(clientData);
        toast.success('Client créé avec succès ! Le client peut maintenant se connecter avec son email et mot de passe.');
      }
      setShowFormModal(false);
      fetchClients();
    } catch (error) {
      toast.error(error.detail || 'Erreur lors de l\'enregistrement du client');
      console.error(error);
    }
  };

  // Format currency for display
  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Export to PDF
  const handleExportPDF = () => {
    if (clients.length === 0) {
      toast.error('Aucun client à exporter');
      return;
    }

    setShowExportMenu(false);

    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const today = new Date().toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    // Header with green background
    doc.setFillColor(130, 196, 60); // Fortuneo green
    doc.rect(0, 0, pageWidth, 25, 'F');

    // Title
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('FORTUNEO BANQUE', 14, 12);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text('Liste des Clients', 14, 20);

    // Date on the right
    doc.setFontSize(10);
    doc.text(`Export du ${today}`, pageWidth - 14, 16, { align: 'right' });

    // Reset text color
    doc.setTextColor(0, 0, 0);

    // Summary box
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(14, 30, pageWidth - 28, 15, 3, 3, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total: ${clients.length} client(s)`, 20, 38);
    doc.setFont('helvetica', 'normal');
    const approved = clients.filter(c => c.status === 'Approuvé').length;
    const pending = clients.filter(c => c.status === 'En attente').length;
    const inProgress = clients.filter(c => c.status === 'En cours').length;
    doc.text(`| Approuvés: ${approved} | En cours: ${inProgress} | En attente: ${pending}`, 70, 38);

    // Table data
    const tableData = clients.map(client => [
      client.id || '',
      `${client.firstName || ''} ${client.lastName || ''}`,
      client.email || '',
      client.phone || '',
      client.profession || '',
      formatMoney(client.monthlyIncome),
      formatMoney(client.loanAmount),
      formatMoney(client.contribution),
      formatMoney(client.receivedAmount),
      `${client.loanDuration || 0} ans`,
      `${(client.interestRate || 0).toFixed(2)}%`,
      formatMoney(client.monthlyPayment),
      client.status || '',
      client.createdAt || ''
    ]);

    // Generate table
    autoTable(doc, {
      startY: 50,
      head: [[
        'ID', 'Nom Complet', 'Email', 'Téléphone', 'Profession',
        'Revenus', 'Prêt', 'Apport', 'Versé', 'Durée', 'Taux', 'Mensualité', 'Statut', 'Date'
      ]],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [130, 196, 60],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 7,
        cellPadding: 2
      },
      alternateRowStyles: {
        fillColor: [245, 250, 240]
      },
      columnStyles: {
        0: { cellWidth: 15 },
        1: { cellWidth: 30 },
        2: { cellWidth: 40 },
        3: { cellWidth: 22 },
        4: { cellWidth: 20 },
        5: { cellWidth: 18, halign: 'right' },
        6: { cellWidth: 20, halign: 'right' },
        7: { cellWidth: 18, halign: 'right' },
        8: { cellWidth: 18, halign: 'right' },
        9: { cellWidth: 14, halign: 'center' },
        10: { cellWidth: 14, halign: 'center' },
        11: { cellWidth: 18, halign: 'right' },
        12: { cellWidth: 18, halign: 'center' },
        13: { cellWidth: 20, halign: 'center' }
      },
      didParseCell: function(data) {
        // Color status cells
        if (data.column.index === 12 && data.section === 'body') {
          const status = data.cell.raw;
          if (status === 'Approuvé') {
            data.cell.styles.textColor = [34, 139, 34];
            data.cell.styles.fontStyle = 'bold';
          } else if (status === 'En attente') {
            data.cell.styles.textColor = [255, 165, 0];
          } else if (status === 'En cours') {
            data.cell.styles.textColor = [65, 105, 225];
          } else if (status === 'Refusé') {
            data.cell.styles.textColor = [220, 20, 60];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Page ${i} / ${pageCount} - Document généré par Fortuneo Banque`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    // Save PDF
    doc.save(`clients_fortuneo_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success(`${clients.length} client(s) exporté(s) en PDF avec succès`);
  };

  // Export to Excel
  const handleExportExcel = () => {
    if (clients.length === 0) {
      toast.error('Aucun client à exporter');
      return;
    }

    setShowExportMenu(false);

    // Create workbook
    const wb = XLSX.utils.book_new();

    // Prepare data with headers and formatting
    const today = new Date().toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    // Create worksheet data
    const wsData = [
      ['FORTUNEO BANQUE - Liste des Clients'],
      [`Export du ${today}`],
      [''],
      [`Total: ${clients.length} client(s) | Approuvés: ${clients.filter(c => c.status === 'Approuvé').length} | En cours: ${clients.filter(c => c.status === 'En cours').length} | En attente: ${clients.filter(c => c.status === 'En attente').length}`],
      [''],
      // Headers row
      [
        'ID',
        'Prénom',
        'Nom',
        'Email',
        'Téléphone',
        'Adresse',
        'Date de naissance',
        'Profession',
        'Revenus mensuels (€)',
        'Montant du prêt (€)',
        'Apport personnel (€)',
        'Versement reçu (€)',
        'Reste à verser (€)',
        'Durée (années)',
        'Taux d\'intérêt (%)',
        'Mensualité (€)',
        'Statut',
        'Date de création',
        'Mot de passe'
      ]
    ];

    // Add client data rows
    clients.forEach(client => {
      const resteAVerser = (client.contribution || 0) - (client.receivedAmount || 0);
      wsData.push([
        client.id || '',
        client.firstName || '',
        client.lastName || '',
        client.email || '',
        client.phone || '',
        client.address || '',
        client.dateOfBirth || '',
        client.profession || '',
        client.monthlyIncome || 0,
        client.loanAmount || 0,
        client.contribution || 0,
        client.receivedAmount || 0,
        resteAVerser,
        client.loanDuration || 0,
        client.interestRate || 0,
        client.monthlyPayment || 0,
        client.status || '',
        client.createdAt || '',
        client.plainPassword || ''
      ]);
    });

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Set column widths
    ws['!cols'] = [
      { wch: 10 },  // ID
      { wch: 15 },  // Prénom
      { wch: 15 },  // Nom
      { wch: 30 },  // Email
      { wch: 18 },  // Téléphone
      { wch: 35 },  // Adresse
      { wch: 15 },  // Date naissance
      { wch: 18 },  // Profession
      { wch: 18 },  // Revenus
      { wch: 18 },  // Prêt
      { wch: 18 },  // Apport
      { wch: 18 },  // Versement reçu
      { wch: 18 },  // Reste à verser
      { wch: 12 },  // Durée
      { wch: 15 },  // Taux
      { wch: 15 },  // Mensualité
      { wch: 12 },  // Statut
      { wch: 15 },  // Date création
      { wch: 18 }   // Mot de passe
    ];

    // Merge cells for title
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 18 } }, // Title row
      { s: { r: 1, c: 0 }, e: { r: 1, c: 18 } }, // Date row
      { s: { r: 3, c: 0 }, e: { r: 3, c: 18 } }  // Summary row
    ];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Clients');

    // Save file
    XLSX.writeFile(wb, `clients_fortuneo_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success(`${clients.length} client(s) exporté(s) en Excel avec succès`);
  };

  const stats = [
    {
      title: 'Total Clients',
      value: clients.length,
      icon: Users,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: 'Dossiers Approuvés',
      value: clients.filter(c => c.status === 'Approuvé').length,
      icon: FileText,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: 'En Cours',
      value: clients.filter(c => c.status === 'En cours').length,
      icon: FileText,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    },
    {
      title: 'En Attente',
      value: clients.filter(c => c.status === 'En attente').length,
      icon: FileText,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              {/* Fortuneo Logo */}
              <img 
                src="https://customer-assets.emergentagent.com/job_fd99e742-6c59-4267-85f6-7d5b0b735f5d/artifacts/83jkoc66_IMG_0229.jpeg" 
                alt="Fortuneo" 
                className="h-12 object-contain"
              />
              <Badge className="ml-4 bg-green-100 text-green-800">Admin</Badge>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/admin/messages')}
                variant="outline"
                size="sm"
                className="flex items-center space-x-2 relative"
                data-testid="messages-btn"
              >
                <Mail size={16} />
                <span>Messages</span>
                {unreadMessages > 0 && (
                  <Badge className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-1.5 py-0.5 min-w-[20px] h-5 flex items-center justify-center">
                    {unreadMessages}
                  </Badge>
                )}
              </Button>
              <Button
                onClick={() => setShowPasswordModal(true)}
                variant="outline"
                size="sm"
                className="flex items-center space-x-2"
                data-testid="admin-settings-btn"
              >
                <Settings size={16} />
                <span>Mot de passe</span>
              </Button>
              <span className="text-sm text-gray-600">Bienvenue, {user?.name}</span>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="flex items-center space-x-2"
              >
                <LogOut size={16} />
                <span>Déconnexion</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold">Modifier le mot de passe admin</h2>
              <Button onClick={() => setShowPasswordModal(false)} variant="ghost" size="sm">
                <X size={20} />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nouveau mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    className="w-full border rounded-lg p-3 pr-12 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Entrez le nouveau mot de passe"
                    data-testid="admin-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={20} /> : <EyeIcon size={20} />}
                  </button>
                </div>
              </div>
              <Button
                onClick={handleSaveAdminPassword}
                disabled={savingPassword}
                className="w-full"
                style={{ backgroundColor: '#82C43C' }}
                data-testid="save-admin-password-btn"
              >
                <Save size={16} className="mr-2" />
                {savingPassword ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-3xl font-bold mt-2">{stat.value}</p>
                  </div>
                  <div className={`${stat.bgColor} p-3 rounded-lg`}>
                    <stat.icon className={stat.color} size={24} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Clients Table */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Gestion des Clients</CardTitle>
                <CardDescription>Liste complète des clients et leurs dossiers</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Button 
                    onClick={() => setShowExportMenu(!showExportMenu)} 
                    variant="outline" 
                    className="flex items-center space-x-2"
                    data-testid="export-btn"
                  >
                    <Download size={16} />
                    <span className="hidden sm:inline">Exporter</span>
                    <ChevronDown size={14} className={`transition-transform ${showExportMenu ? 'rotate-180' : ''}`} />
                  </Button>
                  {showExportMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border z-50">
                      <button
                        onClick={handleExportPDF}
                        className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center gap-3 rounded-t-lg"
                        data-testid="export-pdf-btn"
                      >
                        <FileText size={16} className="text-red-500" />
                        <span>Exporter en PDF</span>
                      </button>
                      <button
                        onClick={handleExportExcel}
                        className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center gap-3 rounded-b-lg border-t"
                        data-testid="export-excel-btn"
                      >
                        <FileText size={16} className="text-green-600" />
                        <span>Exporter en Excel</span>
                      </button>
                    </div>
                  )}
                </div>
                <Button onClick={handleAddClient} className="flex items-center space-x-2">
                  <Plus size={16} />
                  <span className="hidden sm:inline">Nouveau Client</span>
                </Button>
              </div>
            </div>
            <div className="mt-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  placeholder="Rechercher un client..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-semibold text-sm text-gray-600">ID</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm text-gray-600">Nom</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm text-gray-600">Email</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm text-gray-600">Montant Prêt</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm text-gray-600">Statut</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm text-gray-600">Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((client) => (
                    <tr key={client.id} className="border-b hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 text-sm">{client.id}</td>
                      <td className="py-3 px-4 text-sm font-medium">{client.firstName} {client.lastName}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{client.email}</td>
                      <td className="py-3 px-4 text-sm font-semibold">{client.loanAmount.toLocaleString('fr-FR')} €</td>
                      <td className="py-3 px-4 text-sm">
                        <Badge className={getStatusColor(client.status)}>{client.status}</Badge>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">{client.createdAt}</td>
                      <td className="py-3 px-4 text-sm">
                        <div className="flex items-center space-x-2">
                          <Button
                            onClick={() => handleViewClient(client)}
                            variant="ghost"
                            size="sm"
                            className="p-2"
                          >
                            <Eye size={16} />
                          </Button>
                          <Button
                            onClick={() => handleEditClient(client)}
                            variant="ghost"
                            size="sm"
                            className="p-2"
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            onClick={() => handleDeleteClient(client.id)}
                            variant="ghost"
                            size="sm"
                            className="p-2 text-red-600 hover:text-red-700"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
          </>
        )}
      </main>

      {showFormModal && (
        <ClientFormModal
          client={editingClient}
          onClose={() => setShowFormModal(false)}
          onSave={handleSaveClient}
        />
      )}
    </div>
  );
};

export default AdminDashboard;